import React, { useState, useRef, useEffect } from "react";
import { Icon } from "@blueprintjs/core";
import { ScreenProps } from "../../SequenceController";
import { ScreenConfig } from "../Sequences";
import { useLanguage } from "../../../../context/LanguageContext";
import { de as deTranslations } from "../../../../translations/de";
import { en as enTranslations } from "../../../../translations/en";

// --- Hilfsfunktion für Glossar-Alias-Auflösung ---
function resolveGlossarEntry(
    glossar: Record<string, any>,
    key: string
): { key: string; text: string } | undefined {
    const seen = new Set<string>();
    let current = key;
    while (current && !seen.has(current)) {
        seen.add(current);
        const entry = glossar[current];
        if (typeof entry === "string") return { key: current, text: entry };
        if (entry && typeof entry === "object" && "alias" in entry) {
            current = entry.alias;
        } else {
            break;
        }
    }
    return undefined;
}

// Tooltip für Glossar-Begriff (alias-sicher!)
const BegriffTooltip: React.FC<{
    begriff: string;
    children: React.ReactNode;
    glossar: Record<string, any>;
}> = ({ begriff, children, glossar }) => {
    const [visible, setVisible] = useState(false);
    const ref = useRef<HTMLSpanElement>(null);

    useEffect(() => {
        if (!visible) return;
        const onClick = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setVisible(false);
            }
        };
        window.addEventListener("mousedown", onClick);
        return () => window.removeEventListener("mousedown", onClick);
    }, [visible]);

    const resolved = resolveGlossarEntry(glossar, begriff);

    return (
        <span
            ref={ref}
            style={{
                position: "relative",
                cursor: "pointer",
                background: "#e7f1fa",
                color: "#2563eb",
                borderRadius: 5,
                padding: "0 4px",
                fontWeight: 500,
                zIndex: 10,
                display: "inline-block",
            }}
            onMouseEnter={() => setVisible(true)}
            onMouseLeave={() => setVisible(false)}
            onClick={e => {
                setVisible(v => !v);
                e.stopPropagation();
            }}
            tabIndex={0}
            aria-label={`Begriffserklärung für ${begriff}`}
        >
            <>
                {children}
                {visible && (
                    <span
                        style={{
                            position: "absolute",
                            left: "50%",
                            bottom: "100%",
                            transform: "translate(-50%, 12px)",
                            minWidth: 320,
                            maxWidth: 600,
                            background: "#fff",
                            color: "#333",
                            border: `1.5px solid #bcd3f6`,
                            borderRadius: 14,
                            boxShadow: "0 10px 38px 0 rgba(0,0,0,0.17)",
                            zIndex: 1050,
                            padding: "22px 24px",
                            fontWeight: 400,
                            fontSize: 16,
                            lineHeight: 1.6,
                            pointerEvents: "auto",
                            whiteSpace: "pre-wrap",
                            marginTop: 12,
                            textAlign: "justify",
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        <b>{resolved?.key ?? begriff}</b>
                        <br />
                        {resolved?.text ?? begriff}
                    </span>
                )}
            </>
        </span>
    );
};

// --- Glossar-Parsing für Absätze ---
// Now case-insensitive, always maps matched term to the original glossary key
function parseWithGlossarTooltip(
    text: string,
    terms: string[],
    used: Set<string>,
    glossar: Record<string, any>
): React.ReactNode {
    if (typeof text !== "string") return null;

    // Build a mapping from lowercased term to original term
    const glossaryKeyMap: Record<string, string> = {};
    terms.forEach(k => { glossaryKeyMap[k.toLowerCase()] = k; });

    // Case-insensitive regex
    const re = new RegExp(
        `\\b(${terms.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})\\b`,
        "gi"
    );
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    let i = 0;

    while ((match = re.exec(text)) !== null) {
        if (match.index > lastIndex) {
            parts.push(text.substring(lastIndex, match.index));
        }
        const matched = match[1];
        const glossaryKey = glossaryKeyMap[matched.toLowerCase()] || matched;

        if (!used.has(glossaryKey)) {
            used.add(glossaryKey);
            parts.push(
                <BegriffTooltip begriff={glossaryKey} glossar={glossar} key={glossaryKey + "-" + i}>
                    {matched}
                </BegriffTooltip>
            );
        } else {
            parts.push(matched);
        }
        lastIndex = match.index + matched.length;
        i++;
    }
    if (lastIndex < text.length) {
        parts.push(text.substring(lastIndex));
    }
    return parts;
}

const ResearchExampleSection = ({
                                    title,
                                    language,
                                }: {
    title: string;
    language: string;
}) => {
    const introText =
        language === "en"
            ? 'A famous example is the "Asian Disease" experiment by Tversky and Kahneman. Participants were asked how they would respond to a dangerous disease. There are two therapies:'
            : 'Ein berühmtes Beispiel ist das "Asian Disease"-Experiment von Tversky und Kahneman. Die Teilnehmenden sollten entscheiden, wie sie auf eine gefährliche Krankheit reagieren würden. Es gibt zwei Therapien:';

    const gainFrameLabel = language === "en" ? "Gain frame" : "Gewinn-Frame";
    const lossFrameLabel = language === "en" ? "Loss frame" : "Verlust-Frame";
    const safeOptionLabel = language === "en" ? "Safe option" : "Sichere Option";
    const riskyOptionLabel = language === "en" ? "Risky option" : "Riskante Option";

    const programA = language === "en" ? "Program A" : "Programm A";
    const programB = language === "en" ? "Program B" : "Programm B";
    const programC = language === "en" ? "Program C" : "Programm C";
    const programD = language === "en" ? "Program D" : "Programm D";

    const resultText =
        language === "en"
            ? 'Although both statements are mathematically identical, most people in the gain frame chose the safe option ("200 will be saved"): 72% chose it, while only 28% picked the riskier alternative. In the loss frame ("400 will die"), it was the other way around: Only 22% chose the safe, but negatively worded, option, while 78% preferred the riskier alternative.'
            : 'Obwohl beide Aussagen rechnerisch dasselbe bedeuten, wählten die meisten Menschen im Gewinn-Frame die sichere Variante ("200 werden gerettet"): 72 % entschieden sich dafür, nur 28 % wählten die riskantere Alternative. Im Verlust-Frame ("400 sterben") war es umgekehrt: Nur 22 % wählten die sichere, aber negativ formulierte Option, während 78 % die riskantere Alternative bevorzugten.';

    const gainSafe =
        language === "en"
            ? ["200 people", "will be", "saved"]
            : ["200 Menschen", "werden", "gerettet"];
    const gainRisk =
        language === "en"
            ? [
                "1/3 chance all 600",
                "will be saved,",
                "2/3 chance none will be saved",
            ]
            : [
                "1/3 Chance, dass alle 600",
                "gerettet werden,",
                "2/3 Chance, dass niemand gerettet wird",
            ];
    const lossSafe =
        language === "en"
            ? ["400 people", "will", "die"]
            : ["400 Menschen", "werden", "sterben"];
    const lossRisk =
        language === "en"
            ? [
                "1/3 chance no one",
                "will die,",
                "2/3 chance all 600 will die",
            ]
            : [
                "1/3 Chance, dass niemand",
                "stirbt,",
                "2/3 Chance, dass alle 600 sterben",
            ];

    // Helper to render cell with badge at the bottom right (never overlaps text)
    const renderCell = (
        program: string,
        line1: string,
        line2: string,
        line3: string,
        percent: string,
        color: string,
        badgeOpacity = 1
    ) => (
        <div
            style={{
                position: "relative",
                minHeight: 86,
                paddingBottom: 28, // space for badge
                wordBreak: "break-word",
                overflowWrap: "anywhere",
                maxWidth: 280,
                margin: "auto",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
            }}
        >
            <div>
                <div style={{ textAlign: "center", fontWeight: 700 }}>
                    {program}
                </div>
                <div style={{ textAlign: "left" }}>
                    {line1}
                    <br />
                    {line2}
                    <br />
                    {line3}
                </div>
            </div>
            <div
                style={{
                    position: "absolute",
                    right: 14,
                    bottom: 8,
                    background: color,
                    color: "white",
                    padding: "3px 11px",
                    borderRadius: 6,
                    fontSize: 15,
                    fontWeight: 700,
                    opacity: badgeOpacity,
                    boxShadow: "0 1px 6px 0 rgba(0,0,0,0.09)",
                    zIndex: 1,
                    pointerEvents: "none", // so you can still select text
                }}
            >
                {percent}
            </div>
        </div>
    );

    return (
        <div>
            <div style={{ marginTop: 30, marginBottom: 8 }}>
                <b
                    style={{
                        fontSize: 20,
                        color: "#2563eb",
                        fontWeight: 600,
                    }}
                >
                    {title}
                </b>
            </div>

            <p
                style={{
                    marginTop: 0,
                    marginBottom: 22,
                    color: "#555",
                    fontSize: 17,
                    lineHeight: 1.7,
                    textAlign: "justify",
                }}
            >
                {introText}
            </p>

            <div
                style={{
                    border: "1px solid #e2e8f0",
                    borderRadius: 8,
                    overflow: "hidden",
                    marginBottom: 22,
                    boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                    width: "650px",
                    marginLeft: "auto",
                    marginRight: "auto",
                }}
            >
                <table
                    style={{
                        width: "100%",
                        borderCollapse: "separate",
                        borderSpacing: 0,
                        fontSize: 15,
                        background: "#fff",
                        tableLayout: "fixed",
                    }}
                >
                    <colgroup>
                        <col style={{ width: "23%" }} />
                        <col style={{ width: "37%" }} />
                        <col style={{ width: "37%" }} />
                    </colgroup>
                    <thead>
                    <tr>
                        <th
                            style={{
                                padding: "12px 8px",
                                borderBottom: "1px solid #e2e8f0",
                                background: "#f8fafc",
                                textAlign: "left",
                                whiteSpace: "nowrap",
                                fontWeight: 700,
                            }}
                        ></th>
                        <th
                            style={{
                                padding: "12px 16px",
                                borderBottom: "1px solid #e2e8f0",
                                background: "#f8fafc",
                                textAlign: "center",
                                fontWeight: 700
                            }}
                        >
                            {safeOptionLabel}
                        </th>
                        <th
                            style={{
                                padding: "12px 16px",
                                borderBottom: "1px solid #e2e8f0",
                                background: "#f8fafc",
                                textAlign: "center",
                                fontWeight: 700,
                                borderLeft: "2px solid #e2e8f0",
                            }}
                        >
                            {riskyOptionLabel}
                        </th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                        <td
                            style={{
                                padding: "12px 8px",
                                borderBottom: "1px solid #e2e8f0",
                                fontWeight: 600,
                                background: "#f0f7ff",
                                color: "#2563eb",
                                verticalAlign: "top",
                                whiteSpace: "nowrap",
                                textAlign: "left"
                            }}
                        >
                            {gainFrameLabel}
                        </td>
                        <td
                            style={{
                                padding: "12px 16px",
                                borderBottom: "1px solid #e2e8f0",
                                verticalAlign: "top",
                                borderRight: "2px solid #e2e8f0",
                                position: "relative",
                                height: 100
                            }}
                        >
                            {renderCell(
                                programA,
                                gainSafe[0],
                                gainSafe[1],
                                gainSafe[2],
                                "72%",
                                "#2563eb"
                            )}
                        </td>
                        <td
                            style={{
                                padding: "12px 16px",
                                borderBottom: "1px solid #e2e8f0",
                                verticalAlign: "top",
                                position: "relative",
                                height: 100
                            }}
                        >
                            {renderCell(
                                programC,
                                gainRisk[0],
                                gainRisk[1],
                                gainRisk[2],
                                "28%",
                                "#2563eb",
                                0.6
                            )}
                        </td>
                    </tr>
                    <tr>
                        <td
                            style={{
                                padding: "12px 8px",
                                fontWeight: 600,
                                background: "#fff0f3",
                                color: "#e11d48",
                                verticalAlign: "top",
                                whiteSpace: "nowrap",
                                textAlign: "left"
                            }}
                        >
                            {lossFrameLabel}
                        </td>
                        <td
                            style={{
                                padding: "12px 16px",
                                verticalAlign: "top",
                                borderRight: "2px solid #e2e8f0",
                                position: "relative",
                                height: 100
                            }}
                        >
                            {renderCell(
                                programB,
                                lossSafe[0],
                                lossSafe[1],
                                lossSafe[2],
                                "22%",
                                "#e11d48",
                                0.6
                            )}
                        </td>
                        <td
                            style={{
                                padding: "12px 16px",
                                verticalAlign: "top",
                                position: "relative",
                                height: 100
                            }}
                        >
                            {renderCell(
                                programD,
                                lossRisk[0],
                                lossRisk[1],
                                lossRisk[2],
                                "78%",
                                "#e11d48"
                            )}
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>

            <p
                style={{
                    marginTop: 16,
                    marginBottom: 18,
                    color: "#555",
                    fontSize: 17,
                    lineHeight: 1.7,
                    textAlign: "justify",
                }}
            >
                {resultText}
            </p>
        </div>
    );
};

export function ExplanationScreenRiskyChoiceComponent(props: ScreenProps) {
    const { language } = useLanguage();

    useEffect(() => {
        props.setCanContinue(true);
    }, [props]);

    // Use the right translation object
    const translations = language === "en" ? enTranslations : deTranslations;
    const explanation = translations.experiments.RiskyChoiceFraming.screens.explanation;
    const glossarTerms = translations.glossary.terms;

    // Glossar tooltips: get keys and unique 'used' set for all sections
    const terms = Object.keys(glossarTerms).sort((a, b) => b.length - a.length);
    const used = new Set<string>();

    const sections = [
        {
            key: "riskAndUncertainty",
            title: explanation.sections.riskAndUncertainty.title,
            content: explanation.sections.riskAndUncertainty.content,
        },
        {
            key: "everyday",
            title: explanation.sections.everyday.title,
            content: explanation.sections.everyday.content,
        },
        {
            key: "whatIsFraming",
            title: explanation.sections.whatIsFraming.title,
            content: explanation.sections.whatIsFraming.content,
        },
        // researchExample will be rendered separately
        // ownExperiment will be in the highlighted box
    ];

    const parsedContent: React.ReactNode[] = [];

    sections.forEach((section, i) => {
        parsedContent.push(
            <div
                key={"section-title-" + i}
                style={{
                    marginTop: i === 0 ? 0 : 30,
                    marginBottom: 8,
                }}
            >
                <b
                    style={{
                        fontSize: 20,
                        color: "#2563eb",
                        fontWeight: 600,
                    }}
                >
                    {section.title}
                </b>
            </div>
        );

        parsedContent.push(
            <p
                key={`p-${i}`}
                style={{
                    marginTop: 0,
                    marginBottom: 18,
                    whiteSpace: "pre-line",
                    color: "#555",
                    fontSize: 17,
                    lineHeight: 1.7,
                    textAlign: "justify",
                    textJustify: "inter-word",
                }}
            >
                {parseWithGlossarTooltip(section.content, terms, used, glossarTerms)}
            </p>
        );
    });

    // Get experiment note text
    const experimentTitle = explanation.sections.ownExperiment.title;
    const experimentContent = explanation.sections.ownExperiment.content;

    return (
        <div
            className="experiment-container"
            style={{
                maxWidth: 1250,
                margin: "0 auto",
                padding: "24px 20px 44px",
                backgroundColor: "#f8f9fa",
            }}
        >
            {/* Main content card */}
            <div
                style={{
                    background: "#fff",
                    borderRadius: 14,
                    boxShadow: "0 2px 12px rgba(0,0,0,0.09)",
                    padding: "24px 40px 36px",
                    marginBottom: 28,
                }}
            >
                {/* Heading */}
                <h2
                    style={{
                        color: "#2563eb",
                        margin: "0 0 24px 0",
                        fontSize: 28,
                        fontWeight: 700,
                        textAlign: "center",
                    }}
                >
                    {explanation.title}
                </h2>

                <div
                    style={{
                        fontSize: 17,
                        lineHeight: 1.7,
                        position: "relative",
                        minHeight: 320,
                    }}
                >
                    {parsedContent}

                    {/* Custom research example with improved table */}
                    <ResearchExampleSection
                        title={explanation.sections.researchExample.title}
                        language={language}
                    />
                </div>

                {/* Experiment note */}
                <div
                    style={{
                        marginTop: 38,
                        marginBottom: 38,
                        background: "#f8f9fa",
                        borderRadius: 11,
                        padding: "26px 32px",
                        fontSize: 17,
                        lineHeight: 1.7,
                        boxShadow: "0 2px 8px 0 rgba(0,0,0,0.06)",
                        color: "#555",
                        textAlign: "justify",
                        textJustify: "inter-word",
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            alignItems: "flex-start",
                            marginBottom: 16,
                        }}
                    >
                        <Icon
                            icon="lab-test"
                            size={20}
                            style={{
                                marginRight: 12,
                                marginTop: 2,
                                color: "#2563eb",
                            }}
                        />
                        <span
                            style={{
                                fontWeight: 600,
                                fontSize: 18,
                                color: "#2563eb",
                            }}
                        >
                            {experimentTitle}
                        </span>
                    </div>
                    <div
                        dangerouslySetInnerHTML={{
                            __html: experimentContent,
                        }}
                    />
                </div>

                {/* Sources */}
                <div
                    style={{
                        background: "#f8f9fa",
                        borderRadius: 8,
                        padding: "22px 24px",
                        marginTop: 36,
                        fontSize: 15.5,
                        color: "#555",
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            marginBottom: 8,
                        }}
                    >
                        <Icon
                            icon="book"
                            size={16}
                            style={{
                                marginRight: 10,
                                color: "#2563eb",
                            }}
                        />
                        <span
                            style={{
                                fontWeight: 600,
                                color: "#2563eb",
                            }}
                        >
                            {explanation.sources.title}
                        </span>
                    </div>
                    <ul
                        style={{
                            marginTop: 8,
                            marginBottom: 0,
                            paddingLeft: 24,
                            textAlign: "justify",
                            textJustify: "inter-word",
                        }}
                    >
                        {explanation.sources.items.map((src: string, index: number) => (
                            <li
                                key={`source-${index}`}
                                style={{
                                    marginBottom:
                                        index < explanation.sources.items.length - 1
                                            ? 8
                                            : 0,
                                }}
                            >
                                {src}
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    );
}

export const ExplanationScreenRiskyChoice: ScreenConfig = {
    component: ExplanationScreenRiskyChoiceComponent,
    defaultResult: {},
    canContinueDefault: true,
};